<template>
  <form @submit="onSubmit">
    <div :style="{ maxWidth: '40rem' }" class="grid">
      <div v-if="userStore.session === null" class="col-12 pt-0 flex-column">
        <h4 class="font-bold">Ihre Daten:</h4>
      </div>
      <div class="col-12 py-0 sm:col-6 flex flex-column">
        <label for="firstname6">Vorname</label>
        <TableInput id="firstname"
                    :error-message="errors.firstname"
                    :value="firstname"
                    class="w-full"
                    name="firstname" placeholder="" type="text"
                    @update:modelValue="(val) => firstname = val"/>
      </div>
      <div class="col-12 py-0 sm:col-6 flex flex-column">
        <label for="lastname6">Nachname</label>
        <TableInput id="lastname" :error-message="errors.lastname" :value="lastname" class="w-full"
                    name="lastname" placeholder="" type="text"
                    @update:modelValue="(val) => lastname = val"/>
      </div>
      <div class="col-12 py-0 sm:col-6 flex flex-column">
        <label for="email">E-Mail</label>
        <TableInput id="email" :error-message="errors.email" :value="email" class="w-full"
                    name="email" placeholder="" type="email"
                    @update:modelValue="(val) => email = val"/>
      </div>
      <div class="col-12 py-0 sm:col-6 flex flex-column">
        <label for="mobile">Telefonnummer</label>
        <TableInput id="mobile" :error-message="errors.mobile" :value="mobile" class="w-full"
                    name="mobile" placeholder="" type="tel"
                    @update:modelValue="(val) => mobile = val"/>
      </div>
      <div class="col-12 py-0 flex flex-column">
        <label for="mobile">Anmerkung</label>
        <TableTextarea id="mobile" :error-message="errors.comment" :value="comment" class="w-full"
                       name="comment" placeholder="" type="tel"
                       @update:modelValue="(val) => comment = val"/>
      </div>
      <template v-if="createAccount">
        <div class="col-12 pt-2 pb-0 sm:col-6 flex flex-column">
          <label for="password">Passwort</label>
          <TableInput id="password" key="password" :error-message="errors.password"
                      :value="password"
                      class="w-full" name="password" placeholder=""
                      type="password" @update:modelValue="(val) => password = val"/>
        </div>
        <div class="col-12 pt-2 pb-0 sm:col-6 flex flex-column">
          <label for="passwordConfirm">Passwort wiederholen</label>
          <TableInput id="passwordConfirm" key="passwordConfirm"
                      :error-message="errors.passwordConfirm" :value="passwordConfirm"
                      class="w-full" name="passwordConfirm" placeholder=""
                      type="password" @update:modelValue="(val) => passwordConfirm = val"/>
        </div>
      </template>
      <div v-if="userStore.session === null" class="col-12">
        <div class="flex flex-row flex-wrap align-items-center gap-2 mb-2">
          <Checkbox id="createAccount" :binary="true" :model-value="createAccount"
                    name="createAccount" @input="createAccount = $event"/>
          <label class="w-auto font-light" for="zip">Account erstellen</label>
        </div>
        <div v-if="configStore.configObject.shownewsletter === 1" class="flex flex-row flex-wrap align-items-center gap-2">
          <Checkbox id="newsletter" :binary="true" :model-value="newsletter"
                    name="newsletter" @input="newsletter = $event"/>
          <label class="w-auto font-light" for="zip">Newsletter abonnieren?</label>
        </div>
      </div>
    </div>
    <TableButton :label="submitLabel" type="submit" class="mt-3"/>
  </form>
</template>
<script lang="ts" setup>
import Checkbox from "primevue/checkbox";
import TableInput from "@/components/TableInput.vue";
import TableButton from "@/components/TableButton.vue";
import {boolean, object, ref as y_ref, string} from "yup";
import {useForm} from "vee-validate";
import {useConfigStore} from "@/stores/ConfigStore";
import validation_messages from '../validation_messages.json';
import router from "@/router";
import {useRoute} from "vue-router";
import {useUserStore} from "@/stores/UserStore";
import {useTableStore} from "@/stores/TableStore";
import {onMounted, ref, computed} from "vue";
import type {reservation} from "@/types/types";
import TableTextarea from "@/components/TableTextarea.vue";
import {useToast} from "primevue/usetoast";
import {useCrud} from "@/composables/CrudAxios";

const route = useRoute();
const toast = useToast();
const userStore = useUserStore();
const configStore = useConfigStore();
const tableStore = useTableStore();
const loading = ref(false);

const submitLabel = computed(() => tableStore.selectedEvent ? 'Kostenpflichtig bestellen' : 'Reservierung bestätigen');

onMounted(async () => {
  if (userStore.session !== null) {
    for (const [key, value] of Object.entries(userStore.user)) {
      setFieldValue(key, value);
      if (key === 'phone') setFieldValue('mobile', value);
    }
  }
})

const schema = object({
  createAccount: boolean(),
  newsletter: boolean(),
  email: string().required(validation_messages.email).email(validation_messages.email_invalid),
  firstname: string().required(validation_messages.firstname),
  lastname: string().required(validation_messages.lastname),
  mobile: configStore.configObject.phonerequired === 1 ? string().required(validation_messages.mobile) : string(),
  comment: string(),
  password: string().when('createAccount', {
    is: (value: boolean) => value,
    then: (schema) => schema.required(validation_messages.pw),
  }),
  passwordConfirm: string().oneOf([y_ref('password')], validation_messages.pw_dont_match).when('createAccount', {
    is: (value: boolean) => value,
    then: (schema) => schema.required(validation_messages.pw_second_needed),
  }),
});

const {defineField, errors, handleSubmit, setFieldValue} = useForm({
  validationSchema: schema,
});

const [firstname] = defineField('firstname');
const [lastname] = defineField('lastname');
const [email] = defineField('email');
const [mobile] = defineField('mobile');
const [password] = defineField('password');
const [passwordConfirm] = defineField('passwordConfirm');
const [comment] = defineField('comment');
const [createAccount] = defineField('createAccount');
const [newsletter] = defineField('newsletter');

const onSubmit = handleSubmit(async (values) => {
  if (createAccount.value) {
    toast.add({severity: 'info', summary: 'Account wird erstellt..', detail: 'Reservierung wird durchgeführt..', life: 3000});
  } else {
    toast.add({severity: 'info', summary: 'Reservierung wird durchgeführt..', life: 3000});
  }

  loading.value = true;

  // If an event is selected, create an event reservation and redirect to payment
  if ((tableStore as any).selectedEvent) {
    const {postFormAsync} = useCrud();
    const ev: any = (tableStore as any).selectedEvent;
    const reservation_type = Number(ev.event_type || 1);
    const payload: any = {
      ...values,
      ...tableStore.chosenTable,
      eventid: Number(ev.id || ev.eventid),
      reservation_type,
      quantity: reservation_type === 1 ? (tableStore as any).selectedTicketCount : null,
      optionid: reservation_type === 2 ? (tableStore as any).selectedSeatOptionId : null,
    };
    if (userStore.session !== null) payload['uuid'] = userStore.session;

    const response = await postFormAsync(`/extern/table/makeeventreservation/?store=${route.query.store}`, payload);
    // Normalize success flag and reservation id from various possible shapes
    const successFlag = (response?.status?.success ?? response?.success ?? false);
    const ok = successFlag === true || successFlag === 1 || successFlag === '1';
    const reservationId = (response?.status?.resid ?? response?.resid ?? response?.status?.reservationid ?? response?.reservationid ?? null);
    if (ok && reservationId) {
      // Clear any prior toasts and optionally show a short success note
      toast.removeAllGroups();
      toast.add({ severity: 'success', summary: 'Reservierung gespeichert', life: 2500 });
      await router.push({
        name: 'payment',
        query: {
          store: route.query.store as any,
          resid: String(reservationId)
        },
      });
      loading.value = false;
      return;
    } else {
      const message = response?.status?.message || response?.message || 'Reservierung fehlgeschlagen';
      toast.removeAllGroups();
      toast.add({severity: 'error', summary: 'Fehler', detail: message, life: 5000});
      loading.value = false;
      return;
    }
  }

  // Default flow: normal table reservation
  let data = {...values, ...tableStore.chosenTable} as reservation;
  if (userStore.session !== null) data = {...data, 'uuid': userStore.session}
  const response = await userStore.makeReservation(data);

  // Response structure is { status: { success, message, reservationid, resid } } - no .data wrapper
  // Use reservationid (the integer) for display, not resid (the encrypted hash)
  if (response?.status?.success) {
    await router.push({
      name: 'success',
      query: {
        store: route.query.store,
        resid: String(response.status.reservationid)
      },
    })
  }
  // Note: error toast is already shown by makeReservation in UserStore
  loading.value = false;
});
</script>
